import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';

import { StoreInformationService } from '@teem/services/store-information.service';
import { SharedDataService } from '@teem/services/shared-data.service';
import { ACTIONS as INVOICE_ACTIONS } from '@teem/constants/invoice';
import { ONLINE_PAYMENT_LIMIT as _ONLINE_PAYMENT_LIMIT } from '@teem/constants/invoice';
import { Select2Directive } from "@teem/directives/select2.directive";
import { InvoiceService } from '@teem/services/invoice.service';
import { ClientService } from '@teem/services/client.service';
import { LoaderService } from '@teem/services/loader.service';
import { UserService } from '@teem/services/user.service';
import { MixpanelService } from '@teem/services/mixpanel.service';
import { environment } from '@teem/environments/environment';
import { parseDate, copyText } from '@teem/helpers/functions';

import { InvoiceDeliverableForm } from '@teem/model-forms/invoice-deliverable-form';
import { InvoiceDeliverable } from '@teem/models/invoice-deliverable';
import { InvoiceForm } from '@teem/model-forms/invoice-form';
import { Invoice } from '@teem/models/invoice';

import { AdditionalInformationModal } from '@teem/invoices-module/modals/additional-information/additional-information.modal';
import { InvoiceLinkModalComponent } from '@teem/invoices-module/modals/invoice-link-modal/invoice-link-modal.component';
import { InvoiceQrCodeModalComponent } from '@teem/invoices-module/modals/invoice-qr-code-modal/invoice-qr-code-modal.component';
import { OnlinePaymentModal } from '@teem/invoices-module/modals/online-payment/online-payment.modal';
import { PaymentInformationModal } from '@teem/invoices-module/modals/payment-information/payment-information.modal';
import { InvoicePreviewComponent } from '@teem/invoices-module/modals/invoice-preview/invoice-preview.component';
import { AddClientModalComponent } from '@teem/invoices-module/modals/add-client-modal/add-client-modal.component';
import { AddItemsComponent } from '@teem/invoices-module/modals/add-items/add-items.component';

import { messages as i18n } from "@teem/constants/messages";
import * as COUNTRIES from '@teem/constants/country';
import { client as ClientApi, general as GeneralAPI } from '@teem/constants/api';


@Component({
  selector: 'create-invoice-form',
  templateUrl: './create-invoice.component.html',
  styleUrls: ['./create-invoice.scss']
})
export class CreateInvoiceComponent implements OnInit {
  @Output() onCreate: EventEmitter<any> = new EventEmitter();
  @Output() changeTab: EventEmitter<string> = new EventEmitter();
  @Output() onEdit: EventEmitter<string> = new EventEmitter();
  @ViewChild(Select2Directive) select2Directive = null
  @Input() clientUid: string;
  @Input() userInfo: any;
  invoiceForm: FormGroup;
  deliverables: FormArray;
  deliverableForm: FormGroup;
  submitted: boolean = false;
  discountType: string = 'percentage';

  projectTypes: object[] = [
    { value: 'fixed', 'label': $localize`:@@ts.common.fixed:Fixed` },
    { value: 'hourly', 'label': $localize`:@@ts.common.hourly:Hourly` },
  ];
  taxIncludeTypes: any = [
    { name: $localize`:@@ts.common.exclusive_of_tax:Exclusive of Tax`, id: '1' },
    { name: $localize`:@@ts.common.inclusive_of_tax:Inclusive of Tax`, id: '2' },
    { name: $localize`:@@ts.common.not_applicable:Not Applicable`, id: '3' },
  ];
  discountTypes: any = [
    { name: $localize`:@@ts.common.percentage:Percentage`, id: 'percentage' },
    { name: $localize`:@@ts.common.amount:Amount`, id: 'amount' },
  ];
  
  commonData: any;
  isTaxInvoice: boolean = false;
  isTaxInclusiveDropDown: boolean = false;
  taxIncludesValues: any = 2;
  totalPaidAmount: any = 0;
  defaultPaymentInfo: any = {};
  defaultAdditionalInfo: string = '';
  clientListApi: string = ClientApi.list;
  currencyListApi: any = GeneralAPI.currencies;
  selectedClient: any;
  selectedCurrency: any;
  productAndServices: any;
  invoiceDetail: any;
  prevInvoice: boolean = false;
  qrCodeBase64: string = '';
  zatcaEnabled: boolean = false;
  paymentOptions: {
    paypal: boolean,
    stripe: boolean,
    credit_card: boolean,
    stc_pay: boolean,
    bank_transfer: boolean,
    cash: boolean,
    opay: boolean,
    tamara: boolean
  } = {
    paypal: false,
    stripe: false,
    credit_card: false,
    stc_pay: false,
    bank_transfer: false,
    cash: false,
    opay: false,
    tamara: false
  }
  activeTab: boolean = true;
  itemTitle: string = i18n.invoice.add_new_item; //'Add New Item';
  addNewClientTitle: string = i18n.client.add_new_client; //'Add New Client';
  onlinePaymentLimit = _ONLINE_PAYMENT_LIMIT;
  showSaveBtn: boolean = false;
  COUNTRIES: any = COUNTRIES;

  constructor(
    private elRef: ElementRef,
    private fb: FormBuilder,
    private modalService: NgbModal,
    private loaderService: LoaderService,
    private invoiceService: InvoiceService,
    private userService: UserService,
    private clientService: ClientService,
    private sharedDataService: SharedDataService,
    private mixpanelService: MixpanelService,
    private storeInformationService: StoreInformationService) {

  }

  ngOnInit() {
    
    this.invoiceForm = this.fb.group(new InvoiceForm(new Invoice()))
    this.deliverableForm = this.fb.group(new InvoiceDeliverableForm(new InvoiceDeliverable));

    this.invoiceService.commonData().subscribe((data: any) => {
      this.commonData = data;
      this.isTaxInclusivePresent();
      this.invoiceForm.get('invoice_number').setValue(data.next_invoice_number);
      if (data.tax_percent && data.tax_number) {
        this.isTaxInvoice = true;
        this.invoiceForm.get('tax_percent').setValue(parseFloat(data.tax_percent));
        this.invoiceForm.get('include_tax').setValue(true);
        this.enableTax(true);
      } else {
        this.enableTax(false);
        this.taxIncludeTypes = [{ name: $localize`:@@ts.common.not_applicable:Not Applicable`, id: '3' }];
        this.invoiceForm.get('tax_type').setValue(3);
        // this.taxIncludeTypes.splice(0, 1);
      }

      this.selectedCurrency = {
        id: this.commonData?.currencies?.currency_code,
        currency_code: this.commonData?.currencies?.currency_code,
        currency_symbol: this.commonData?.currencies?.currency_symbol,
        currency_full_name: this.commonData?.currencies?.currency_full_name
      }

      this.zatcaEnabled = this.commonData?.zatca_enabled;
      this.userInfo.currency_symbol = this.selectedCurrency?.currency_symbol;

      if (this.clientUid) {
        this.clientService.get(this.clientUid).subscribe((client) => {
          this.selectedClient = {
            id: client.id,
            full_name: client.full_name,
            email: client.email
          }

          this.invoiceForm.get('client_id').setValue(client.id);
        })
      }
    });

    this.userService.getSetting({ key: ['payment_info', 'additional_info'] }).subscribe((data: any) => {
      this.defaultAdditionalInfo = data.additional_info ? data.additional_info : '';

      if (this.userInfo.accept_bank_transfer) {
        this.defaultPaymentInfo = data.payment_info ? data.payment_info : {};
      }
      // this.invoiceForm.get('payment_info').setValue(this.defaultPaymentInfo);
      // this.invoiceForm.get('additional_info').setValue(this.defaultAdditionalInfo)
    })

    this.fetchProductAndServices();
    this.validateBuyerInfo();

    // if (this.isProPlan || this.isProfessionalPlan || this.isBusinessPlan) {
    //   if (this.userInfo.halala_mobile) {
    //     this.invoiceForm.get('include_online_payment').setValue(true);
    //     this.invoiceForm.get('online_payment_percent').setValue(100);
    //   }
    //   this.fetchProductAndServices();
    // } else {
    //   this.productAndServices = [{ 'id': '__add_new_items__', 'text': this.itemTitle }];
    // }

    // let node = this.elRef.nativeElement.parentNode.parentNode.parentNode.parentNode.parentNode.parentNode.parentNode.parentNode;
    // node.parentElement.classList.remove("simplified-invoice");
    // node.parentElement.classList.remove("simplified-invoice--shadow");
    // node.parentElement.classList.add("content-background");
    
  }

  fetchProductAndServices() {
    this.invoiceService.getProductAndServices().subscribe((data: any) => {
      if (data.length) {
        data.unshift({ 'id': '__add_new_items__', 'text': this.itemTitle })
        this.productAndServices = data;
      } else {
        this.productAndServices = [{ 'id': '__add_new_items__', 'text': this.itemTitle }];
      }
    });
  }

  changeInvoiceType(event) {
    let invoiceType = this.invoiceForm.get('invoice_type');
    invoiceType.setValue(event);
    this.validateBuyerInfo();
  }

  validateBuyerInfo() {
    if (this.invoiceForm.controls.invoice_type.value == 'b2b') {
      ['invoice_time', 'buyer_first_name', 'buyer_last_name', 'buyer_address', 'buyer_vat_number', 'buyer_cr_number']?.forEach((field: string) => {
        if (field == 'buyer_vat_number') {
          this.invoiceForm.get(field).setValidators([Validators.required, Validators.pattern(/^3\d{13}3$/)]);
        } else {
          this.invoiceForm.get(field).setValidators([Validators.required]);
        }
        this.invoiceForm.get(field).updateValueAndValidity();
      });
    } else {
      ['invoice_time', 'buyer_first_name', 'buyer_last_name', 'buyer_address', 'buyer_vat_number', 'buyer_cr_number']?.forEach((field: string) => {
        this.invoiceForm.get(field).setValue(null);
        this.invoiceForm.get(field).clearValidators();
        this.invoiceForm.get(field).updateValueAndValidity();
      });
    }
  }

  changeQuantity(deliverable, type) {
    let value = deliverable.controls.quantity.value;
    if (type == 'plus') {
      if (deliverable?.value?.available_quantity <= value  && deliverable?.value?.type == 'product') {
        Swal.fire({title: i18n.warning, text: i18n.invoice.out_of_stock, icon: 'warning' })
        return false;
      }
      deliverable.controls.quantity.setValue(value + 1);
    } else if (type == 'minus' && value > 1) {
      deliverable.controls.quantity.setValue(value - 1);
    }
  }

  changeInputQuantityIfValueNullOrZero(deliverable) {
    let value = deliverable.controls.quantity.value;
    if (value == null || value <= 0) {
      deliverable.controls.quantity.setValue(1);
    }
  }

  changeInputQuantity(deliverable) {
    let value = deliverable.controls.quantity.value;
    if (deliverable?.value?.available_quantity < value  && deliverable?.value?.type == 'product') {
      Swal.fire({
        title: i18n.warning,
        text: i18n.invoice.out_of_stock,
        icon: 'warning'
      })
      deliverable.controls.quantity.setValue(deliverable?.value?.available_quantity);
    }
  }

  changeDiscount(value: number) {
    this.calculateOriginalAmount();
    this.calculateDiscountedAmount();
  }

  changeDiscountType(event) {
    this.discountType = event;
    let discountAmount = this.invoiceForm.get('discount');
    
    if (event != 'percentage') {
      discountAmount.clearValidators();
    } else {
      discountAmount.setValidators([Validators.min(0),Validators.max(100)]);
    }
    discountAmount.updateValueAndValidity();
    this.invoiceForm.get('discount_type').setValue(event);
    this.calculateOriginalAmount();
    this.calculateDiscountedAmount();
  }

  isTaxInclusive(event) {
    if (event == 1) {
      this.invoiceForm.get('include_tax').setValue(true);
      this.isTaxable(true);
    }
    if (event == 2) {
      this.invoiceForm.get('include_tax').setValue(true);
      this.isTaxable(true);
    }
    if (event == 3) {
      this.invoiceForm.get('include_tax').setValue(false);
      this.isTaxable(false);      
    }

    let taxPercent = this.invoiceForm.get('tax_percent');
    if (event == 3) {
      taxPercent.setValue(null);
      taxPercent.clearValidators();
    } else {
      taxPercent.setValidators([Validators.required,Validators.min(0),Validators.max(100)]);
    }
    taxPercent.updateValueAndValidity();

    this.calculateOriginalAmount();
    this.calculateDiscountedAmount();
  }

  get taxInvoice() {
    const invoiceFormValues = this.invoiceForm.value;
    if (this.taxAmount || (invoiceFormValues.tax_type == 1 || invoiceFormValues.tax_type == 2)) {
      return true;
    }
    return false;
  }
  
  get prefTextColor() {
    return `${this.userInfo.preference.text_color} !important;`;
  }

  get taxAmount() {
    let items = this.deliverables ? this.deliverables.value : [];
    if (items && items.length > 0) {
      if (this.discountType == 'percentage') {
        return items.reduce((a, b) => a + this.deliverableTaxWithDiscountAmount(b), 0);
      }
      let itemsTotal = items.reduce((a, b) => a + this.deliverableCost(b), 0);
      return this.deliverableTaxWithDiscountByAmount(itemsTotal);
    }
    return 0;
  }
  
  get newDeliverableTax() {
    const invoiceFormValues = this.invoiceForm.value;
    if (invoiceFormValues.include_tax && invoiceFormValues.tax_percent) {
      if (invoiceFormValues.tax_type == 1) {
        return (this.invoiceOriginalAmount * invoiceFormValues.tax_percent / 100);
      } else if (invoiceFormValues.tax_type == 2) {
        return (((this.invoiceOriginalAmount) / (1 + invoiceFormValues.tax_percent / 100)) * (invoiceFormValues.tax_percent / 100))
      }
    }
    return 0;
  }

  deliverableTax(deliverable: any) {
    let value = deliverable.quantity * deliverable.rate;
    const invoiceFormValues = this.invoiceForm.value;
    if (invoiceFormValues.tax_type == 2) {
      if ((invoiceFormValues.include_tax && invoiceFormValues.tax_percent && deliverable.is_taxable)) {
        return ((value / (1 + invoiceFormValues.tax_percent / 100)) * (invoiceFormValues.tax_percent / 100))
      }
      return 0;
    } else if (invoiceFormValues.tax_type == 3) {
      return 0;
    }
    return (invoiceFormValues.include_tax && invoiceFormValues.tax_percent && deliverable.is_taxable) ? (value * invoiceFormValues.tax_percent / 100) : 0;
  }

  deliverableTaxWithDiscountAmount(deliverable: any) {
    let itemTotal = deliverable.quantity * deliverable.rate;
    let discountAmount = this.invoiceForm.get('discount').value;
    discountAmount = parseFloat(discountAmount).toFixed(2);
    if (discountAmount > 0) {
      if (this.discountType == 'percentage') {
        discountAmount = ((itemTotal / (100) * (discountAmount)));
      }
    } else {
      discountAmount = 0;
    }
    
    let value = itemTotal - discountAmount;
    const invoiceFormValues = this.invoiceForm.value;
    if (invoiceFormValues.tax_type == 2) {
      if ((invoiceFormValues.include_tax && invoiceFormValues.tax_percent && deliverable.is_taxable)) {
        return ((value / (1 + invoiceFormValues.tax_percent / 100)) * (invoiceFormValues.tax_percent / 100))
      }
      return 0;
    } else if (invoiceFormValues.tax_type == 3) {
      return 0;
    }
    return (invoiceFormValues.include_tax && invoiceFormValues.tax_percent && deliverable.is_taxable) ? (value * invoiceFormValues.tax_percent / 100) : 0;
  }

  deliverableTaxWithDiscountByAmount(itemTotal: any) {
    let discountAmount = this.invoiceForm.get('discount').value;
    discountAmount = parseFloat(discountAmount).toFixed(2);
    
    let value = itemTotal - discountAmount;
    const invoiceFormValues = this.invoiceForm.value;
    if (invoiceFormValues.tax_type == 2) {
      if ((invoiceFormValues.include_tax && invoiceFormValues.tax_percent)) {
        return ((value / (1 + invoiceFormValues.tax_percent / 100)) * (invoiceFormValues.tax_percent / 100))
      }
      return 0;
    } else if (invoiceFormValues.tax_type == 3) {
      return 0;
    }
    return (invoiceFormValues.include_tax && invoiceFormValues.tax_percent) ? (value * invoiceFormValues.tax_percent / 100) : 0;
  }

  onChangeClient(value: any) {
    if (value) {
      this.mixpanelService.track('invoice_create_start');

      if (value.id == '__add_new_items__') {
        this.select2Directive.clear();
        const modalRef = this.modalService.open(AddClientModalComponent);

        // Set id on modal dialog
        modalRef.componentInstance.el.nativeElement.parentNode.parentNode.parentNode.id = "newClient";

        modalRef.result.then(result => {
          this.selectedClient = {
            id: result.id,
            full_name: result.full_name,
            email: result.email
          }
          this.invoiceForm.get('client_id').setValue(result.id)
        });
      } else {
        this.selectedClient = {
          id: value.id,
          full_name: value.full_name,
          email: value.email
        }

        if (this.invoiceForm?.value?.invoice_type == 'b2b') {
          const firstName = value?.buyer_first_name?.trim() ? value.buyer_first_name : value?.first_name;
          const lastName = value?.buyer_last_name?.trim() ? value.buyer_last_name : value?.last_name;
          const vatNumber = value?.buyer_vat_number?.trim() ? value.buyer_vat_number : value?.tax_reg_number;
          const address = value?.buyer_address?.trim() ? value.buyer_address : value?.address;

          this.invoiceForm.get('buyer_first_name').setValue(firstName);
          this.invoiceForm.get('buyer_last_name').setValue(lastName);
          this.invoiceForm.get('buyer_address').setValue(address);
          this.invoiceForm.get('buyer_vat_number').setValue(vatNumber);
          this.invoiceForm.get('buyer_cr_number').setValue(value?.buyer_cr_number);
        }
      }
    }
  }

  onChangeCurrency(value: any) {
    if (value) {
      this.selectedCurrency = {
        id: value.currency_code,
        currency_code: value.currency_code,
        currency_symbol: value.currency_symbol,
        currency_full_name: value?.currency_full_name
      }
      this.userInfo.currency_symbol = this.selectedCurrency?.currency_symbol;
    }
  }

  addDeliverable(form: FormGroup) {
    let items = this.deliverables ? this.deliverables.value : [];
    const index = items.findIndex((e) => e.title === form.value.title);
    if (index === -1) {
      form.value.available_quantity = (form.value.available_quantity == undefined || form.value.available_quantity == null) ? undefined : form.value.available_quantity;
      const currentDeliverbles = this.invoiceForm.get('items') as FormArray
      currentDeliverbles.push(
        this.fb.group(
          new InvoiceDeliverableForm(new InvoiceDeliverable(form.value))
        )
      )

      this.deliverables = this.invoiceForm.get('items') as FormArray;
      setTimeout(() => {
        if (form.value.type && this.invoiceForm.controls.include_tax.value) {
          this.enableTax(true);
        }
        form.controls.is_taxable.setValue((this.invoiceForm.controls.include_tax.value > 0) ? 1 : 0);
      }, 100);
    } else {
      this.changeQuantity(this.deliverables.controls[index], 'plus');
    }

    if (!this.zatcaEnabled) {
      this.generateQrCode();
    }
  }

  removeDeliverable(index: number) {
    const currentDeliverbles = this.invoiceForm.get('items') as FormArray;
    currentDeliverbles.removeAt(index)
    this.deliverables = currentDeliverbles;
  }

  enableTax(value: boolean) {
    let taxPercentControl = this.invoiceForm.get('tax_percent');
    if (value) {
      taxPercentControl.setValidators([Validators.required]);
    } else {
      taxPercentControl.setValue(null);
      taxPercentControl.clearValidators();
    }
    taxPercentControl.updateValueAndValidity();

    const deliverables = this.invoiceForm.get('items') as FormArray;

    let control;
    for (control of deliverables?.controls) {
      control.controls.is_taxable.setValue(value ? 1 : 0);
    }

    this.deliverableForm.controls.is_taxable.setValue(value ? 1 : 0);
    this.deliverables = deliverables;
  }

  isTaxable(value: boolean) {
    const deliverables = this.invoiceForm.get('items') as FormArray;
    let control;
    for (control of deliverables?.controls) {
      control.controls.is_taxable.setValue(value ? 1 : 0);
    }
    this.deliverableForm.controls.is_taxable.setValue(value ? 1 : 0);
    this.deliverables = deliverables;
  }

  validate() {
    this.showSaveBtn = false;
    this.prevInvoice = false;
    let deliverables = this.invoiceForm.get('items').value;
    // if (deliverables.length == 0) {
    //   Swal.fire({
    //     title: i18n.warning,
    //     text: i18n.invoice.please_add_deliverable,
    //     icon: 'warning'
    //   })
    // } else {
    //   this.submitted = true;
    //   if (this.invoiceForm.valid) {
    //     return true
    //   }
    // }
    if (deliverables.length == 0) {
      Swal.fire({
        title: i18n.warning,
        text: i18n.invoice.please_add_deliverable,
        icon: 'warning'
      })
    } 
    this.submitted = true;
    if (this.invoiceForm.valid && deliverables.length > 0) {
      return true
    }
    return false;
  }
  
  saveAsDraft() {
    if (this.validate()) {
      this.invoiceForm.get('is_draft').setValue(1);
      this.invoiceForm.get('action').setValue('draft');
      this.isInvoiceTotalExceeds();
    }
  }

  saveInvoice() {
    if (this.validate()) {
      this.invoiceForm.get('is_draft').setValue(0);
      this.invoiceForm.get('action').setValue(null);
      this.isInvoiceTotalExceeds();
    }
  }

  saveInvoiceSend() {
    if (this.validate()) {
      this.invoiceForm.get('is_draft').setValue(0);
      if (this.selectedClient?.email) {
        this.invoiceForm.get('action').setValue(INVOICE_ACTIONS.EMAIL);
      } else {
        this.invoiceForm.get('action').setValue(INVOICE_ACTIONS.WHATSAPP);
      }
      this.isInvoiceTotalExceeds();
    }
  }

  // editInvoice() {
  //   let invoiceFormData: any = this.invoiceForm;
  //   this.onEdit.emit(invoiceFormData);
  // }

  // saveAsPdf() {
  //   if (this.validate()) {
  //     try {
  //       this.invoiceForm.get('is_draft').setValue(0);
  //       this.invoiceForm.get('action').setValue('pdf');
  //       this.isInvoiceTotalExceeds();

  //       // (window as any).open(`${environment.baseUrl}/download/invoice/${data.uuid}/pdf?lang=${(window as any).LOCALE}`, '_self', 'location=yes');
  //     } catch (err) {
  //       console.error('promise error')
  //     }
  //   }
  // }

  // saveAndEmail() {
  //   if (this.validate()) {
  //     this.invoiceForm.get('is_draft').setValue(0);
  //     this.invoiceForm.get('action').setValue(INVOICE_ACTIONS.EMAIL);
  //     this.isInvoiceTotalExceeds();
  //   }
  // }

  // saveAndWhatsapp() {
  //   if (this.validate()) {
  //     this.invoiceForm.get('is_draft').setValue(0);
  //     this.invoiceForm.get('action').setValue(INVOICE_ACTIONS.WHATSAPP);
  //     this.isInvoiceTotalExceeds();
  //   }
  // }


  // saveAndShareLink() {
  //   if (this.validate()) {
  //     this.invoiceForm.get('is_draft').setValue(0);
  //     this.invoiceForm.get('action').setValue(INVOICE_ACTIONS.SHARE_LINK);
  //     this.isInvoiceTotalExceeds();
  //   }
  // }

  private submit() {
    this.calculateOriginalAmount();
    this.calculateDiscountedAmount();
    this.invoiceForm.get('total_amount').setValue(this.invoiceTotal);

    return new Promise((resolve, reject) => {
      this.loaderService.startLoading();

      let fd: any = this.invoiceForm.value;
      fd.invoice_date = parseDate(fd.invoice_date);

      // Create Invoice
      this.invoiceService.create(fd).subscribe((r) => {
        this.loaderService.stopLoading();

        let msg = i18n.invoice.invoice_saved;
        let result: any = r;

        if (fd.action == INVOICE_ACTIONS.WHATSAPP) {
          msg = i18n.invoice.invoice_saved_and_whatsapp_sent;
        } else if (fd.action == INVOICE_ACTIONS.EMAIL) {
          msg = i18n.invoice.invoice_saved_and_email_sent
        } else if (fd.action == INVOICE_ACTIONS.SHARE_LINK) {
          msg = i18n.invoice.invoice_saved
          // msg = result.url;
          // copyText(msg);
        }

        Swal.fire(i18n.done, msg, 'success').then(() => {
          if (result.url)
            this.generateInvoiceLink(result.url);

          if (fd.action == 'pdf' && result.uuid) {
            (window as any).open(`${environment.baseUrl}/download-invoice/${result.uuid}/pdf?lang=${(window as any).LOCALE}`, '_blank');
            // (window as any).open(`${environment.baseUrl}/download/invoice/${result.uuid}/pdf?lang=${(window as any).LOCALE}`, '_self');
          }
          this.invoiceForm = this.fb.group(new InvoiceForm(new Invoice()))
          this.deliverables = this.invoiceForm.get('items') as FormArray;

          if (result.uuid && result.online_payment && !result.url)
            this.generateInvoiceQrCode(result.online_payment);

          this.onCreate.emit(fd);
        });
        this.submitted = false;
        resolve(r);
      }, (err) => {
        this.loaderService.stopLoading();

        // Set server side error
        if (err.status === 422) {
          if (err.error && err.error.errors && Object.keys(err.error.errors).length) {
            let validationErrors = err.error.errors;

            Object.keys(validationErrors).forEach((prop) => {
              this.invoiceForm.get(prop)?.setErrors({
                serverError: validationErrors[prop]
              })
            })
          }
        }

        Swal.fire(i18n.alert.error, i18n.getError(err), 'error');
      })
    });
  }

  // openAddClientModal() {
  //   const modalRef = this.modalService.open(AddClientModalComponent);

  //   // Set id on modal dialog
  //   modalRef.componentInstance.el.nativeElement.parentNode.parentNode.parentNode.id = "newClient";

  //   modalRef.result.then(result => {
  //     this.selectedClient = {
  //       id: result.id,
  //       full_name: result.full_name,
  //       email: result.email
  //     }

  //     this.invoiceForm.get('client_id').setValue(result.id)
  //     // this.clientsTableRef.getPage(1);
  //   }, fallbackResult => {
  //     // do nothing
  //   })

  // }


  generateQrCode() {
    if (this.taxIncludesValues != 3) {
      this.invoiceDetail = this.invoiceForm.value;
      this.invoiceDetail.client = this.selectedClient;
      this.invoiceDetail.invoice_date = parseDate(this.invoiceDetail.invoice_date);    
      if (this.userInfo.country_id == COUNTRIES.SAUDI_ARABIA) {
        this.invoiceService.getInvoiceQrCode(this.invoiceDetail).subscribe((data: any) => {
          this.loaderService.stopLoading();
          if (data.status == 'success') {
            this.qrCodeBase64 = data.qr_code_base64;
          }
        }, err => {
          this.loaderService.stopLoading();
          Swal.fire(i18n.alert.error, i18n.getError(err), 'error');
        })
      }
    }    
  }

  generateInvoiceLink(invoiceUrl) {
    const modalRef = this.modalService.open(InvoiceLinkModalComponent);

    // Set id on modal dialog
    modalRef.componentInstance.el.nativeElement.parentNode.parentNode.parentElement.id = "quick-payment";
    modalRef.componentInstance.invoiceUrl = invoiceUrl;
    modalRef.componentInstance.onSuccess = () => {
      //
    };

    modalRef.result.then(result => {
      //
    }, fallbackResult => {
      //
    });
  }

  generateInvoiceQrCode(invoiceUrl) {
    const modalRef = this.modalService.open(InvoiceQrCodeModalComponent);

    // Set id on modal dialog
    modalRef.componentInstance.el.nativeElement.parentNode.parentNode.parentElement.id = "quick-payment";
    modalRef.componentInstance.invoiceUrl = invoiceUrl;
    modalRef.componentInstance.onSuccess = () => {
      //
    };

    modalRef.result.then(result => {
      //
    }, fallbackResult => {
      //
    });
  }

  closeModel() {
    if (this.prevInvoice) {
      this.prevInvoice = false;
      this.elRef.nativeElement.parentNode.parentNode.parentNode.parentNode.parentNode.parentNode.parentNode.parentNode.parentElement.classList.remove("simplified-preview");
    } else {
      this.changeTab.emit('all-invoices')
    }
  }

  maxDiscountValidation(){
    let discountAmount = this.invoiceForm.get('discount');
    if (this.discountType != 'percentage') {
      discountAmount.clearValidators();
      discountAmount.setValidators([Validators.min(0),Validators.max(this.subTotalDeliverableCost)]);
    } else {
      discountAmount.clearValidators();
      discountAmount.setValidators([Validators.min(0),Validators.max(100)]);
    }
    discountAmount.updateValueAndValidity();
  }

  isInvoiceTotalExceeds() {
    if(this.invoiceTotal > 0){
      if (this.userInfo.accept_credit_card && this.userInfo.is_contract_sign && (this.invoiceTotal < 1 || this.invoiceTotal >= this.onlinePaymentLimit)) {
        Swal.fire({
          text: i18n.invoice.credit_card_payments_will_be_hidden_as_a_payment_method_if_your_invoice_total_exceeds,
          icon: 'warning',
          showCloseButton: true,
          focusConfirm: false,
          confirmButtonText: i18n.ok,
        }).then(result => {
          if (result.value) {
            this.submit();
          }
        })
      } else {
        this.submit();
      }
    }else{
      Swal.fire({
        text: i18n.invoice.invoice_amount_must_be_greater_than_zero,
        icon: 'warning',
        showCloseButton: true,
        focusConfirm: false,
        confirmButtonText: i18n.ok,
      })
    }
  }

  isTaxInclusivePresent() {
    this.commonData.tax_percent == null ? this.commonData.tax_percent = 0 : this.commonData.tax_percent;
    return this.commonData.tax_percent == 0 ? false : true;
  }

  get taxAmountToInvoiceTotal() {
    const invoiceFormValues = this.invoiceForm.value;
    if (invoiceFormValues.tax_type == 1) {
      let items = this.deliverables ? this.deliverables.value : [];
      if (items && items.length > 0) {
        // return items.reduce((a, b) => a + this.deliverableTaxWithDiscountAmount(b), 0);
        if (this.discountType == 'percentage') {
          return items.reduce((a, b) => a + this.deliverableTaxWithDiscountAmount(b), 0);
        }
        let itemsTotal = items.reduce((a, b) => a + this.deliverableCost(b), 0);
        return this.deliverableTaxWithDiscountByAmount(itemsTotal);
      }
    }
    return 0;
  }

  deliverableCost(deliverableValues: any) {
    let value = deliverableValues.quantity * deliverableValues.rate;
    if (!isNaN(value)) {
      return value;
    }
    return 0;
  }

  // deliverableCost(deliverableValues: any) {
  //   let value = deliverableValues.quantity * deliverableValues.rate;
  //   const invoiceFormValues = this.invoiceForm.value;
  //   if (!isNaN(value)) {
  //     if (invoiceFormValues.include_tax && invoiceFormValues.tax_percent && deliverableValues.is_taxable) {
  //       if (invoiceFormValues.tax_type == 2 || invoiceFormValues.tax_type == 3) {
  //         value = value;// + (value / (1 + (invoiceFormValues.tax_percent / 100)));
  //       } else {
  //         value = value + value * invoiceFormValues.tax_percent / 100;
  //       }
  //     }
  //     return value;
  //   }
  //   return 0;
  // }

  calculateOriginalAmount() {
    this.invoiceForm.get('original_amount').setValue(this.invoiceOriginalAmount);
    this.maxDiscountValidation();
    if (!this.zatcaEnabled) {
      this.generateQrCode();
    }
  }

  calculateDiscountedAmount() {
    this.invoiceForm.get('discounted_amount').setValue(this.invoiceDiscountAmount);
  }

  // get totalDeliverableTax() {
  //   const invoiceFormValues = this.invoiceForm.value;
  //   if (invoiceFormValues.include_tax && invoiceFormValues.tax_percent) {
  //     if (invoiceFormValues.tax_type == 1) {
  //       return (this.subTotalDeliverableCost * invoiceFormValues.tax_percent / 100);
  //     } else if (invoiceFormValues.tax_type == 2) {
  //       return (((this.subTotalDeliverableCost) / (1 + invoiceFormValues.tax_percent / 100)) * (invoiceFormValues.tax_percent / 100))
  //     }
  //   }
  //   return 0;
  // }

  get invoiceTotal() {
    let value = this.invoiceOriginalAmount;
    const invoiceFormValues = this.invoiceForm.value;
    if (invoiceFormValues.include_tax && invoiceFormValues.tax_percent) {
      if (invoiceFormValues.tax_type == 3 || invoiceFormValues.tax_type == 2) {
        value = value;
      } else {
        // value = value + (value * invoiceFormValues.tax_percent / 100);
        value = value + this.taxAmountToInvoiceTotal;
      }      
    }
    return value + this.invoiceDeliveryFeeAmount;
  }

  get invoiceOriginalAmount() {
    return (this.subTotalDeliverableCost - this.invoiceDiscountAmount);
  }

  get invoiceDiscountAmount() {
    let discountAmount = this.invoiceForm.get('discount').value;
    discountAmount = parseFloat(discountAmount).toFixed(2);
    if (discountAmount > 0) {
      if (this.discountType != 'percentage') {
        return discountAmount;
      }
      return (((this.subTotalDeliverableCost) / (100) * (discountAmount)));
    }
    return 0;
  }

  get invoiceDeliveryFeeAmount() {
    if (this.invoiceForm.get('delivery_fee').value > 0) {
      return this.invoiceForm.get('delivery_fee').value;
    }
    return 0;
  }

  get subTotalDeliverableCost(){
    let items = this.deliverables ? this.deliverables.value : [];
    if (items && items.length > 0) {
      return items.reduce((a, b) => a + this.deliverableCost(b), 0);
    }
    return 0;
  }

  // get invoiceOriginalAmount() {
  //   let discountAmount = this.invoiceForm.get('discount').value;
  //   discountAmount = parseFloat(discountAmount).toFixed(2);
  //   if (discountAmount > 0) {
  //     let items = this.deliverables ? this.deliverables.value : [];
  //     if (items && items.length > 0) {
  //       return items.reduce((a, b) => a + this.deliverableCost(b), 0);
  //     }
  //     return null;
  //   } else {
  //     return null;
  //   }
  // }

  // get invoiceDiscountAmount() {
  //   let discountAmount = this.invoiceForm.get('discount').value;
  //   discountAmount = parseFloat(discountAmount).toFixed(2);
  //   if (discountAmount > 0) {
  //     if (this.discountType != 'percentage') {
  //       return discountAmount;
  //     }
  //     return (((this.invoiceOriginalAmount) / (100) * (discountAmount)));
  //   } else {
  //     return null;
  //   }
  // }

  // get invoiceDeliveryFeeAmount() {
  //   if (this.invoiceForm.get('delivery_fee').value > 0) {
  //     return this.invoiceForm.get('delivery_fee').value;
  //   } else {
  //     return null;
  //   }
  // }

  // toggleSaveBtn() {
  //   this.showSaveBtn = true;
  // }

  // setInvoiceWidth(condition) {
  //   $("#left-section-pdf").hide();
  //   $("#left-section-invoice").hide();
  //   $(".invoice-screen-type").removeClass("active");
  //   $(".page-size").removeClass('page-size--mobile');
  //   $(".page-size").removeClass('page-size--pdf');
  //   $(".prev-invoice__bottomInfo").removeClass("prev-invoice__bottomInfo--mobile");
  //   $("body").removeClass("mobile--size");
  //   $("body").removeClass("pdf--size");
  //   $("body").removeClass("web--size");

  //   if (condition == "mobile") {
  //     $(".ist-1").addClass("active");
  //     $(".page-size").addClass('page-size--mobile');
  //     $(".prev-invoice__bottomInfo").addClass('prev-invoice__bottomInfo--mobile');
  //     $("body").addClass("mobile--size");
  //     $("#left-section-invoice").show();
  //   } else if (condition == "web") {
  //     $(".ist-3").addClass("active");
  //     $("body").addClass("web--size");
  //     $("#left-section-invoice").show();
  //   } else if (condition == "pdf") {
  //     $("#left-section-pdf").hide();
  //     $(".page-size").addClass('page-size--pdf');
  //     var invoice, node = document.getElementsByClassName("prev-invoice__leftSection")[0];
  //     $(".prev-invoice__editsave").toggle(); 

  //     $("body").addClass("pdf--size");
  //     $("#left-section-pdf").show(); 
  //     $(".ist-2").addClass("active");
  //   }
  // }

}


